import { Box, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { LogoWithIcon } from 'src/UI/app-container/header/Header';
import { Button, Link, TextInput } from 'src/components';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';
import { AuthCard } from 'src/auth/components/AuthCard';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

interface RecoveryEmailFormProps {
  defaultEmail: string;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (email: string) => void;
}

export const RecoveryEmailForm = ({
  defaultEmail,
  error,
  isSubmitting,
  onSubmit,
}: RecoveryEmailFormProps) => {
  const form = useForm<EmailFormValues>({
    mode: 'controlled',
    initialValues: {
      email: defaultEmail,
    },
    validate: zodResolver(emailSchema),
    validateInputOnBlur: true,
  });

  const handleSubmit = (values: EmailFormValues) => {
    onSubmit(values.email);
  };

  return (
    <AuthCard>
      <Stack gap="md">
        <Box mb="lg">
          <LogoWithIcon textColor="gray.10" />
        </Box>

        <Text fz="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
          Forgot your password?
        </Text>

        <Text fz="md" fw={FONT_WEIGHTS.medium} c="gray.10" mb="xs">
          No worries! Let&apos;s get you back in.
        </Text>

        <AuthFeedback error={error} />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Email"
              placeholder="name@example.com"
              withAsterisk
              autoFocus
              key={form.key('email')}
              {...form.getInputProps('email')}
            />

            <Button
              type="submit"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting || !form.isValid() || form.values.email.trim() === ''}
              variant="primary"
            >
              Send reset code
            </Button>
          </Stack>
        </form>

        <Text ta="center" size="sm">
          <Link to="/login" c="orange" size="textSm">
            Remember your password? Log in
          </Link>
        </Text>
      </Stack>
    </AuthCard>
  );
};
